import { Component, Vue, Watch } from "vue-property-decorator";
import {
  MIRA_ALL_CLIENTS_SCOPE,
  MIRA_BRANCH_NONE,
  MIRA_BRANCH_OTHER,
  MIRA_EXCLUDED_KEY,
  MiraAvailabilityState,
  MiraBranchDocument,
  MiraPublishedPeriod,
  MiraSection,
  MiraSectionDocument,
  MiraTrendSeries,
} from "@/data/models/Mira";
import {
  getBranchesOfClient,
  getClientId,
  getClientInfo,
  getMemberClientIds,
  getMemberClientsInformationOfGroup,
} from "@/data/repository/ClientRepository";
import {
  isMiraGroupMaster,
  loadBranchSection,
  loadCompanyMemberSection,
  loadGroupViewSection,
  loadMarketSection,
  loadMemberSection,
  loadMiraSections,
} from "@/data/repository/MiraRepository";
import { withMarketOfOwnKind } from "@/utils/MiraMarketKind";
import { companyViewOf, companyViewRoster } from "@/utils/MiraCompanyView";
import { getEffectiveUser } from "@/utils/ViewAs";
import { miraPercentilesAccess } from "@/permissions/UserPermissions";
import { MiraComparisonModule } from "@/store/modules/MiraComparisonModule";
import { MiraPeriodModule } from "@/store/modules/MiraPeriodModule";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  COLOR_MIRA_MARKET_BAR,
  COLOR_MIRA_MARKET_BAR_DARK,
  COLOR_MIRA_REFERENCE_BAR,
  COLOR_MIRA_REFERENCE_BAR_DARK,
  COLOR_TABLE_HEADER,
  COLOR_TABLE_HEADER_DARK,
} from "@/utils/colors";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { formatFloat } from "@/utils/LocalisationHelper";
import {
  MIRA_COMPARISON_BRANCHES,
  MIRA_COMPARISON_GROUP,
  MIRA_COMPARISON_MARKET,
  MIRA_COMPARISON_MEMBERS,
  MIRA_COMPARISON_NONE,
  MiraComparisonMode,
  MIRA_COMPARISON_REFERENCE_ID,
  MIRA_MARKET_OWN_ID,
  MIRA_MARKET_PROJECTS_ID,
  MiraComparisonView,
  buildBranchComparison,
  buildComparison,
  buildMarketComparison,
  membersSuffix,
} from "@/utils/MiraComparisonView";
import { MIRA_GROUP_VIEW_ID, MIRA_GROUP_VIEW_OWN_ID, buildGroupViewComparison } from "@/utils/MiraGroupViewComparison";
import { MiraSectionUnlocks, availablePeriods, collectUnlocks, miraPeriodName, periodRange } from "@/utils/MiraDataQuality";
import { MiraTile, publishedMetric, tidyCaption } from "@/utils/MiraDataQualityView";
import { setMiraToolbarTitle } from "@/utils/MiraEdition";
import { effectivePeriod } from "@/utils/MiraOverview";
import { MiraDryingContext } from "@/utils/MiraDryingView";
import { MiraSizedEntity, MiraStackEntity, buildStableEntityColors, comparisonUsesLevels } from "@/utils/MiraStackedTrendView";
import { hasTrend, isTotalMetric } from "@/utils/MiraTrendView";
import { themed } from "@/utils/Theme";
import { formatNumber } from "@/utils/Utils";

/**
 * The figure that sizes an entity for its stable comparison colour: its projects in the chosen period.
 * Read from the business-development document (the member's own, or the branch document), never the
 * section on screen, so the colour order is the same on every Mira screen.
 */
const MIRA_SIZE_METRIC_ID = "MIRA-PRJ-001";

/**
 * What every Mira section screen shares: loading the section documents, the period picker, the header
 * lines (the period, its comparison, the run date), and the context the view models translate and
 * format through. A screen extends it and names its section (`section`) and its app bar title
 * (`titleKey`); everything specific to its figures stays in the screen and its view model.
 *
 * A base class rather than a copy per screen, so the period rules (the document decides which periods
 * exist, the rolling year first) cannot differ between Datenqualität and Trocknung.
 */
@Component
export default class MiraScreenBase extends Vue {
  isLoading = true;
  sections: Partial<Record<MiraSection, MiraSectionDocument>> = {};

  /**
   * The figure whose timeline the Verlauf card shows, set by clicking a period tile. `null` means the
   * default (the first period tile that has a trend); see {@link activeTrendMetricId}. Initialised with
   * `null`, never `undefined`, so it is reactive (ui-conventions, Reactivity).
   */
  selectedTrendMetricId: string | null = null;

  /**
   * Whether the signed-in client is a group master, so the Vergleich selector and the group comparison
   * apply. Read once on mount (it does not change in-session); a data field, not a getter, so the
   * selector appears after the load rather than on the loading frame. Initialised `false`.
   */
  isGroupMaster = false;

  /** The member clients' display names, keyed by client id. Filled when the comparison is turned on. */
  memberNames: Record<string, string> = {};

  /** Each member's own section document for this screen, keyed by client id (`null` when it has none). */
  memberSectionDocs: Record<string, MiraSectionDocument | null> = {};

  /**
   * Each member's own business-development document, keyed by client id, for the size that orders the
   * stable comparison colours (its projects in the chosen period). Loaded alongside the section
   * documents, cached in the repository; `null` where a member has none. Initialised `{}`, not
   * `undefined`, so it is reactive.
   */
  memberBusinessDocs: Record<string, MiraSectionDocument | null> = {};

  /**
   * The signed-in client's branch business-development document, for the size that orders the branches'
   * stable comparison colours. Loaded once on mount off a non-master, cached; `null` where there is none.
   * Initialised `null`, not `undefined`, so it is reactive.
   */
  branchBusinessDocument: MiraBranchDocument | null = null;

  /** Whether the member documents are being loaded, for the comparison card's single spinner. */
  membersLoading = false;

  /** Guards the one-time member load per screen visit; the repository caches across screens. */
  membersLoaded = false;

  /**
   * The signed-in client's branch comparison document for this screen, or `null` where there is none (a
   * single-branch client, Datenqualität, or a group master). Loaded once on mount so the "Standorte"
   * option can appear; initialised `null`, never `undefined`, so it is reactive (ui-conventions).
   */
  branchDocument: MiraBranchDocument | null = null;

  /**
   * The Markt document of this screen's section (#2610072): every project of every published Mandant once.
   * Loaded on mount (one read, cached for the session) so the "Markt" option can appear; `null` where there
   * is none. Markt je Mandant (`miraMarket/clients_*`) is no longer read (Hans, #2610112 X6a).
   */
  marketProjectsDocument: MiraSectionDocument | null = null;

  /**
   * A group member's view of its group for this screen's section (`groupView_{section}`, #2610112 X6d),
   * loaded on mount off a master so the "Gruppe" option can appear; `null` where there is none.
   */
  groupViewDocument: MiraSectionDocument | null = null;

  /** The section this screen shows. Each screen overrides it. */
  get section(): MiraSection {
    return MiraSection.DATA_QUALITY;
  }

  /** The i18n key of the app bar title. Each screen overrides it. */
  get titleKey(): string {
    return "pages.mira";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /**
   * What the view models need. Reads `$vuetify.theme.dark` here, so every getter built on it re-renders
   * when the theme changes.
   */
  get viewContext(): MiraDryingContext {
    return {
      t: (key, params) => this.$t(key, params ?? []).toString(),
      te: (key) => this.$te(key),
      formatNumber: (value) => formatNumber(value),
      formatDecimal: (value, digits) => formatFloat(value, digits),
      dark: this.$vuetify.theme.dark,
      locale: this.$i18n.locale,
    };
  }

  get document(): MiraSectionDocument | null {
    return this.sections[this.section] ?? null;
  }

  /**
   * The screen's KPI tiles. The base returns none; each screen overrides it (a subclass getter
   * replaces the base one in vue-class-component). Declared here so the base can split them into the
   * "Stand heute" block and the period tiles, and drive the Verlauf selection, without each screen
   * repeating that logic.
   */
  get tiles(): MiraTile[] {
    return [];
  }

  /**
   * The tiles scoped to the chosen period, rendered under the period header. On a group master each
   * tile's caption gains the "über X von Y Betrieben" suffix where the figure carries its member
   * counts; on a client document the suffix is absent, so this is a no-op there.
   *
   * Every Mira figure is now a figure of the period ("Mira is a look back", #2610040): the "Stand heute"
   * snapshot block was retired, so there is no snapshot tile to split off here anymore.
   */
  get periodTiles(): MiraTile[] {
    return this.tiles
      .filter((tile) => !this.showMarketComparison || this.inMarket(tile.id))
      .filter((tile) => !this.showGroupViewComparison || this.inGroupView(tile.id))
      .map((tile) => this.withMembersSuffix({ ...tile, caption: tidyCaption(tile.caption) }))
      .map((tile) => this.withExceptionCaption(tile))
      .map((tile) => this.withMarketCaption(tile));
  }

  /**
   * Appends "n als Ausnahme ausgelassen" where IRES Cloud left projects out of the figure because their
   * event was recorded late (Mira-Ausnahmen, Hans R8, #2610040): the count travels as the figure's
   * `breakdown.excluded`. Shown to everyone who sees the tile (Taimoor, 2026-10-16).
   */
  withExceptionCaption(tile: MiraTile): MiraTile {
    const excluded = this.period?.metrics[tile.id]?.breakdown?.[MIRA_EXCLUDED_KEY] ?? 0;
    if (!(excluded > 0)) {
      return tile;
    }
    const note = this.$t("mira.exceptions.leftOut", [formatNumber(excluded)]).toString();
    return { ...tile, caption: tile.caption ? `${tile.caption} · ${note}` : note };
  }

  /** Appends the group's "über X von Y Betrieben" suffix to a tile's caption, where it applies. */
  withMembersSuffix(tile: MiraTile): MiraTile {
    const suffix = membersSuffix(this.period?.metrics[tile.id]?.members, this.viewContext);
    if (!suffix) {
      return tile;
    }
    return { ...tile, caption: tile.caption ? `${tile.caption} · ${suffix}` : suffix };
  }

  /**
   * In Markt mode only, appends a short "Markt X" caption (the market median) to a tile that carries a
   * market block, so the default view is unchanged. Gated on the stored comparison (the store), not on
   * {@link comparisonMode} or {@link showMarketComparison}, to avoid a computed cycle with
   * {@link marketAvailable}, which decides the offered options. Without a Markt value no caption is added,
   * so a stored "Markt" this screen does not offer adds nothing.
   */
  withMarketCaption(tile: MiraTile): MiraTile {
    if (this.storedComparisonMode !== MIRA_COMPARISON_MARKET || this.isAllClients) {
      return tile;
    }
    // The Markt je Projekt value of the figure, beside the company's own (#2610072).
    const market = publishedMetric(this.marketProjectsDocument?.periods[this.selectedPeriod] ?? null, tile.id);
    if (!market || market.value === null || market.value === undefined) {
      return tile;
    }
    const marketText = this.$t("mira.market.tileCaption", [this.trendFormatter(tile.id)(market.value)]).toString();
    return { ...tile, caption: tile.caption ? `${tile.caption} · ${marketText}` : marketText };
  }

  /** Whether the user sees the P15 / P85 percentiles. Reactive: a self role change takes effect in-session. */
  get showPercentiles(): boolean {
    return miraPercentilesAccess(getEffectiveUser());
  }

  /** The timelines the document carries, or `undefined` for an older document without them. */
  get trends(): Record<string, MiraTrendSeries> | undefined {
    return this.document?.trends;
  }

  /** Whether a figure has a usable timeline (so its tile is selectable and the Verlauf card can show it). */
  trendAvailable(metricId: string): boolean {
    return hasTrend(this.trends, metricId);
  }

  /**
   * A period tile is selectable when it has a trend (so the Verlauf card can show it). In comparison mode
   * **every** period tile is selectable, so a figure without a timeline can still be compared across the
   * group.
   */
  isTileSelectable(tile: MiraTile): boolean {
    // In Markt mode too, every tile stays selectable (Taimoor, 2026-10-27): a figure without a Markt opens
    // the card with the "keinen Marktwert" note rather than a tile that cannot be clicked.
    return this.comparisonActive ? true : this.trendAvailable(tile.id);
  }

  /**
   * Whether the Markt carries the figure at all in the chosen period, with a value or withheld for too few
   * companies. A figure it never carries (a count or total, the GZ-timed Prozess figures) has no Markt by
   * rule, so its tile is hidden in Markt mode (Taimoor, 2026-10-27); one withheld today stays, with its note.
   */
  inMarket(metricId: string): boolean {
    return this.marketProjectsDocument?.periods[this.selectedPeriod]?.metrics[metricId] !== undefined;
  }

  /**
   * Whether the member's group view carries the figure in the chosen period, with a value or withheld. A
   * figure a group never carries (one that cannot be pooled) is hidden in "Gruppe" mode, as in Markt mode.
   */
  inGroupView(metricId: string): boolean {
    return this.groupViewDocument?.periods[this.selectedPeriod]?.metrics[metricId] !== undefined;
  }

  /** Whether the Markt has a value for the figure in the chosen period (#2610072). */
  hasMarketValue(metricId: string): boolean {
    return publishedMetric(this.marketProjectsDocument?.periods[this.selectedPeriod] ?? null, metricId) !== null;
  }

  /**
   * The figure the Verlauf card shows: the clicked one where it still has a trend among the period
   * tiles, else the first period tile that has one, else none (the card is hidden). Reading the
   * period tiles first keeps the default following a period change.
   */
  get activeTrendMetricId(): string | null {
    const selectable = this.periodTiles.filter((tile) => this.trendAvailable(tile.id)).map((tile) => tile.id);
    const selected = this.selectedTrendMetricId;
    if (selected && selectable.includes(selected)) {
      return selected;
    }
    return selectable[0] ?? null;
  }

  /** The tile whose figure the Verlauf card shows, for its title. */
  get activeTrendTile(): MiraTile | null {
    const active = this.activeTrendMetricId;
    return active ? this.periodTiles.find((tile) => tile.id === active) ?? null : null;
  }

  /** "Verlauf: <tile title>", the Verlauf card's title. */
  get trendCardTitle(): string {
    return this.activeTrendTile ? this.$t("mira.trend.title", [this.activeTrendTile.title]).toString() : "";
  }

  // --- Comparison (Vergleich): the member clients of a group, #2610009 ---

  /**
   * The comparison chosen last, shared across the Mira screens through the store. It outlives a sign-out
   * and a move to another screen, so it may name an option this client or screen does not offer: read
   * {@link comparisonMode} instead, except where that would be a computed cycle.
   */
  get storedComparisonMode(): MiraComparisonMode {
    return MiraComparisonModule.mode;
  }

  /**
   * The comparison in effect: the chosen one where this screen offers it, else "Kein Vergleich" (Taimoor,
   * #2610112: Klünder's "Standorte" reached SVT Master Client after a sign-in, which offers no branches, so
   * the selector showed as active with nothing ticked). Every reader of the comparison goes through this.
   */
  get comparisonMode(): MiraComparisonMode {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const stored = this.storedComparisonMode;
    const options = this.comparisonOptions;
    return options.includes(stored) ? stored : MIRA_COMPARISON_NONE;
  }

  /**
   * The comparisons the selector offers. A group master gets "Mandanten der Gruppe" (its view is the
   * group, never its own branches); any other client gets "Standorte" where this screen has a branch
   * document. Those two never appear together. "Markt" sits beside whichever applies (it is orthogonal),
   * offered to any client and to a master whenever a figure on this screen carries a market block for the
   * chosen period.
   */
  get comparisonOptions(): MiraComparisonMode[] {
    const options: MiraComparisonMode[] = [MIRA_COMPARISON_NONE];
    // "Alle Mandanten" offers no comparison for now (#2610111, D5).
    if (this.isAllClients) {
      return options;
    }
    if (this.isGroupMaster) {
      options.push(MIRA_COMPARISON_MEMBERS);
    } else if (this.branchDocument) {
      options.push(MIRA_COMPARISON_BRANCHES);
    }
    if (this.groupViewAvailable) {
      options.push(MIRA_COMPARISON_GROUP);
    }
    if (this.marketAvailable) {
      options.push(MIRA_COMPARISON_MARKET);
    }
    return options;
  }

  /**
   * Whether any period tile of this screen carries a market block for the chosen period, so the "Markt"
   * option can appear. Read off the raw {@link tiles} (not {@link periodTiles}) so the Markt caption those
   * add cannot feed back into this getter.
   */
  get marketAvailable(): boolean {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const tiles = this.tiles;
    if (!this.period || this.isAllClients) {
      return false;
    }
    return tiles.some((tile) => this.hasMarketValue(tile.id));
  }

  /**
   * Whether "Gruppe" can be offered (#2610112 X6d): a member (never a master, whose comparison is its
   * members) whose group view has a value for any period tile of this screen in the chosen period.
   */
  get groupViewAvailable(): boolean {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const tiles = this.tiles;
    const groupView = this.groupViewDocument;
    if (!groupView || !this.period || this.isGroupMaster || this.isAllClients) {
      return false;
    }
    return tiles.some((tile) => publishedMetric(groupView.periods[this.selectedPeriod] ?? null, tile.id) !== null);
  }

  /** The group comparison of a member is on: "Gruppe" chosen and offered. */
  get showGroupViewComparison(): boolean {
    return this.comparisonMode === MIRA_COMPARISON_GROUP && this.groupViewAvailable;
  }

  /** The real branches of the signed-in client, name by id (deleted branches already dropped). */
  get branchNames(): Record<string, string> {
    const names: Record<string, string> = {};
    getBranchesOfClient().forEach((branch) => {
      names[branch.id] = branch.name;
    });
    return names;
  }

  /**
   * The branch reference row's label: the signed-in client's own name (its total across all branches),
   * falling back to "Gesamt" where no name is cached. Chosen over a bare "Gesamt" so it reads like the
   * group view's "Gruppe gesamt".
   */
  get branchReferenceLabel(): string {
    const info = getClientInfo();
    const name = info?.shortName || info?.companyName || info?.clientName;
    return name || this.$t("mira.comparison.referenceClient").toString();
  }

  /** The name of a comparison option, for the selector and the header. */
  comparisonName(mode: MiraComparisonMode): string {
    return this.$t(`mira.comparison.modes.${mode}`).toString();
  }

  /** "Vergleich: Mandanten der Gruppe" on the sticky header's first line, only when a comparison is on. */
  get comparisonChoiceLabel(): string {
    // An IRES account's combined view says so where a comparison would be named (#2610111).
    if (this.isAllClients) {
      return this.$t("mira.allClients.label").toString();
    }
    return this.comparisonMode === MIRA_COMPARISON_NONE
      ? ""
      : this.$t("mira.comparison.choice", [this.comparisonName(this.comparisonMode)]).toString();
  }

  /** The membership caveat under the header, where the chosen period starts before the group was recorded. */
  get membershipNote(): string {
    const seededAt = this.document?.membershipSeededAt;
    if (!this.period?.membershipAssumed || !seededAt) {
      return "";
    }
    return this.$t("mira.comparison.membershipAssumed", [formatDateToDayMonthYear(new Date(seededAt))]).toString();
  }

  /** The member comparison is on: a group master with "Mandanten der Gruppe" chosen. */
  get showMemberComparison(): boolean {
    return this.isGroupMaster && !this.isAllClients && this.comparisonMode === MIRA_COMPARISON_MEMBERS;
  }

  /**
   * The branch comparison is on: a non-master with "Standorte" chosen and a branch document present. The
   * document guard matters because the chosen mode is shared across screens: a user who picks "Standorte"
   * on Geräte and moves to Datenqualität (no branch document) must fall back to the Verlauf card there.
   */
  get showBranchComparison(): boolean {
    return !this.isGroupMaster && !this.isAllClients && !!this.branchDocument && this.comparisonMode === MIRA_COMPARISON_BRANCHES;
  }

  /** Whether the comparison card replaces the Verlauf card (a member, branch or Markt comparison chosen). */
  get showComparison(): boolean {
    return this.showMemberComparison || this.showBranchComparison || this.showMarketComparison || this.showGroupViewComparison;
  }

  /**
   * The Markt comparison is on: "Markt" chosen and a figure on the screen has a Markt value. It draws the
   * comparison cards like the group view (#2610072): the company against "Markt je Projekt" and "Markt je
   * Mandant".
   */
  get showMarketComparison(): boolean {
    return this.comparisonMode === MIRA_COMPARISON_MARKET && this.marketAvailable;
  }

  /**
   * Whether any comparison (member, branch or market) is on, so every period tile is selectable (a figure
   * needs no timeline to be comparable) and the chosen tile, not the Verlauf one, drives the card.
   */
  get comparisonActive(): boolean {
    return this.showComparison;
  }

  /** The Markt row's label ("Markt"; "Markt je Projekt" until #2610137). */
  get marketLabel(): string {
    return this.$t("mira.market.entities.projects").toString();
  }

  /**
   * The figure the comparison card shows: the clicked period tile where it still exists, else the first
   * period tile. Unlike {@link activeTrendMetricId}, a figure needs no timeline to be comparable, so any
   * period tile qualifies.
   */
  get activeComparisonMetricId(): string | null {
    const ids = this.periodTiles.map((tile) => tile.id);
    const selected = this.selectedTrendMetricId;
    if (selected && ids.includes(selected)) {
      return selected;
    }
    return ids[0] ?? null;
  }

  /** The tile highlighted as selected: the comparison figure in any comparison mode, else the Verlauf figure. */
  get selectedTileId(): string | null {
    return this.comparisonActive ? this.activeComparisonMetricId : this.activeTrendMetricId;
  }

  /** "<figure title> nach Mandant" (members) or "… nach Standort" (branches), the comparison card's title. */
  get comparisonCardTitle(): string {
    const active = this.activeComparisonMetricId;
    const tile = active ? this.periodTiles.find((candidate) => candidate.id === active) ?? null : null;
    if (!tile) {
      return "";
    }
    const key = this.showMarketComparison
      ? "mira.market.cardTitle"
      : this.showGroupViewComparison
      ? "mira.comparison.cardTitleGroupView"
      : this.showBranchComparison
      ? "mira.comparison.cardTitleBranches"
      : "mira.comparison.cardTitle";
    return this.$t(key, [tile.title]).toString();
  }

  /** The empty-state wording, in branch terms where the branch comparison is on, else the member default. */
  get comparisonEmptyText(): string | null {
    if (this.showMarketComparison) {
      return this.$t("mira.market.empty").toString();
    }
    if (this.showGroupViewComparison) {
      return this.$t("mira.comparison.groupView.empty").toString();
    }
    return this.showBranchComparison ? this.$t("mira.comparison.emptyBranches").toString() : null;
  }

  /** The comparison card shows its single spinner while the member documents load. */
  get comparisonLoading(): boolean {
    return this.membersLoading;
  }

  /**
   * The comparison the card draws. In branch mode: the client's own total, one bar per branch the document
   * carries, and the branches with no value. Otherwise the member comparison: the group total, one bar per
   * member, and the members with no value.
   */
  get comparisonView(): MiraComparisonView | null {
    const metricId = this.activeComparisonMetricId;
    if (!metricId) {
      return null;
    }
    if (this.showMarketComparison) {
      return buildMarketComparison(
        metricId,
        this.document,
        this.marketProjectsDocument,
        this.marketLabel,
        this.selectedPeriod,
        this.period?.comparesTo,
        this.trendFormatter(metricId),
        this.viewContext,
        this.branchReferenceLabel,
        this.showPercentiles
      );
    }
    if (this.showGroupViewComparison) {
      return buildGroupViewComparison(
        metricId,
        this.document,
        this.groupViewDocument,
        this.$t("mira.comparison.referenceGroup").toString(),
        this.selectedPeriod,
        this.period?.comparesTo,
        this.trendFormatter(metricId),
        this.viewContext,
        this.branchReferenceLabel,
        this.showPercentiles
      );
    }
    if (this.showBranchComparison) {
      return buildBranchComparison(
        metricId,
        this.branchDocument,
        this.document,
        this.branchNames,
        this.selectedPeriod,
        this.period?.comparesTo,
        this.trendFormatter(metricId),
        this.viewContext,
        this.branchReferenceLabel,
        this.showPercentiles
      );
    }
    return buildComparison(
      metricId,
      this.document,
      this.memberSectionDocs,
      this.memberNames,
      this.selectedPeriod,
      this.period?.comparesTo,
      this.trendFormatter(metricId),
      this.viewContext,
      this.$t("mira.comparison.referenceGroup").toString(),
      this.showPercentiles
    );
  }

  // --- Comparison bars (the Verlauf position): a total compared per member / branch, #2610009 ---

  /**
   * Whether the grouped comparison bars are drawn in the Verlauf position: a comparison is on and the
   * chosen figure is comparable this way. A **total** always is (one sum bar per entity). An **average /
   * share** is too (one mean / share bar per entity, hidden under the case floor; Taimoor, 2026-10-05),
   * but only where at least one entity actually carries a trend for it: an older branch document, or one
   * IRES Cloud has not yet deployed the average trends to, carries none, and the ranking card alone shows.
   * A segment figure never reaches here: it is no period tile. Whether the ranking card follows the bars
   * is {@link showRankingCard}'s call.
   */
  get showComparisonBars(): boolean {
    const metricId = this.activeComparisonMetricId;
    if (!this.showComparison || !metricId || this.comparisonBarsEntities.length === 0) {
      return false;
    }
    if (isTotalMetric(metricId)) {
      return true;
    }
    // An average is compared at Quartal / Jahr only, so the bars need quarter or year history on both the
    // reference (it scaffolds the periods) and at least one entity; a figure kept by month only (the fleet
    // utilisation) falls back to the ranking card instead of an empty chart.
    const hasCoarse = (series?: MiraTrendSeries) => (series?.quarter?.length ?? 0) > 0 || (series?.year?.length ?? 0) > 0;
    return hasCoarse(this.trends?.[metricId]) && this.comparisonBarsEntities.some((entity) => hasCoarse(entity.series));
  }

  /** The reference's own timelines the bars scaffold on: the group's (members) or the client's (branches). */
  get comparisonBarsReferenceTrends(): Record<string, MiraTrendSeries> | undefined {
    return this.trends;
  }

  /**
   * The entities the comparison draws a bar for: one per member (its own document's timeline) or per
   * branch the branch document carries a timeline for (plus the "Ohne Standort" / "Weitere Standorte"
   * buckets), worst first and best on the right (#2610137). Up to eight get a bar each; more draw the five
   * levels, in the builder (#2610112). For an average or share the whole comes first as the gesamt bar
   * ({@link comparisonReferenceEntity}).
   */
  get comparisonBarsEntities(): MiraStackEntity[] {
    const metricId = this.activeComparisonMetricId;
    if (!metricId) {
      return [];
    }
    // The broadest scope on the left, the company's own on the right (Hans, #2610137; Taimoor: Markt, Gruppe,
    // Mandant, Standort, from bigger to lower). Colours follow the entity, not the position.
    if (this.showMarketComparison) {
      // The Markt beside the company's own bar, over the company's periods (#2610072).
      return [
        { id: MIRA_MARKET_PROJECTS_ID, label: this.marketLabel, series: this.marketProjectsDocument?.trends?.[metricId] },
        { id: MIRA_MARKET_OWN_ID, label: this.branchReferenceLabel, series: this.trends?.[metricId] },
      ];
    }
    if (this.showGroupViewComparison) {
      // The member's group beside its own bar, over its own periods.
      return [
        { id: MIRA_GROUP_VIEW_ID, label: this.$t("mira.comparison.referenceGroup").toString(), series: this.groupViewDocument?.trends?.[metricId] },
        { id: MIRA_GROUP_VIEW_OWN_ID, label: this.branchReferenceLabel, series: this.trends?.[metricId] },
      ];
    }
    const reference = this.comparisonReferenceEntity(metricId);
    return [...(reference ? [reference] : []), ...this.worstFirst(this.comparedEntities(metricId))];
  }

  /**
   * The gesamt bar before the Standorte or Mandanten (Hans, #2610137: "Trocknungsdauer gesamt / Standort 1 /
   * Standort 2"): the company's or the group's own figure, as named in the Rangliste. Averages and shares
   * only: a total stays the figure in the card header, never a bar beside its parts (Hans, #2610112 X1).
   */
  private comparisonReferenceEntity(metricId: string): MiraStackEntity | null {
    if (isTotalMetric(metricId)) {
      return null;
    }
    return {
      id: MIRA_COMPARISON_REFERENCE_ID,
      label: this.comparisonView?.reference?.label ?? this.branchReferenceLabel,
      series: this.trends?.[metricId],
      reference: true,
    };
  }

  /** The members or branches of the comparison for one figure, unordered. */
  private comparedEntities(metricId: string): MiraStackEntity[] {
    if (this.showBranchComparison) {
      const branchTrends = this.branchDocument?.trends ?? {};
      const names = this.branchNames;
      return Object.keys(branchTrends).map((id) => ({
        id,
        label:
          id === MIRA_BRANCH_NONE
            ? this.$t("mira.comparison.branchNone").toString()
            : id === MIRA_BRANCH_OTHER
            ? this.$t("mira.comparison.branchOther").toString()
            : names[id] ?? this.$t("mira.comparison.branchUnknown").toString(),
        series: branchTrends[id]?.[metricId],
      }));
    }
    return Object.keys(this.memberNames).map((id) => ({
      id,
      label: this.memberNames[id],
      series: this.memberSectionDocs[id]?.trends?.[metricId],
    }));
  }

  /**
   * The entities in the ranking's order turned round: worst first, best on the right (Hans, #2610137: "right
   * and top is better than left and bottom"; the Rangliste keeps the best at the top). An entity without a
   * value in the chosen period stands before the worst, by name.
   */
  private worstFirst(entities: MiraStackEntity[]): MiraStackEntity[] {
    const order = (this.comparisonView?.rows ?? []).map((row) => row.id);
    const rank = (id: string) => (order.includes(id) ? order.indexOf(id) : order.length);
    return [...entities].sort((a, b) => rank(b.id) - rank(a.id) || a.label.localeCompare(b.label));
  }

  /**
   * More members or branches than the timeline names (#2610112, above eight): it draws the five levels and
   * the ranking card below names everyone. Never in the Markt, which has two entities.
   */
  get comparisonUsesLevels(): boolean {
    // The gesamt bar is drawn beside the levels, never counted in them.
    const segments = this.comparisonBarsEntities.filter((entity) => !entity.reference);
    return !this.showMarketComparison && comparisonUsesLevels(segments.length);
  }

  /**
   * Whether the ranking card shows below the timeline (Hans and Taimoor, #2610112 X1 / X4: a card that shows
   * nothing the timeline does not is dropped). Shown in the Markt and a member's "Gruppe" (their markers are their own), where the
   * timeline draws the levels and so names no one, and where there is no timeline for the figure at all
   * (the card then carries the figure, or says why a branch comparison has none).
   */
  get showRankingCard(): boolean {
    return (
      this.showComparison &&
      (this.showMarketComparison || this.showGroupViewComparison || !this.showComparisonBars || this.comparisonUsesLevels)
    );
  }

  /**
   * The reference's figure for the chosen period ("Klünder Trocknungstechnik GmbH: 5.638", "Gruppe gesamt:
   * 7,3 Tage") in the timeline's header, where the ranking card that carried it is not shown (X1: the total
   * as a figure, never as a bar). `null` otherwise or without a value.
   */
  get comparisonHeadline(): string | null {
    const reference = this.comparisonView?.reference ?? null;
    // Where the gesamt bar is drawn, the legend names its figure instead.
    if (!reference || this.showRankingCard || this.comparisonBarsEntities.some((entity) => entity.reference)) {
      return null;
    }
    return this.$t("mira.comparison.headline", [reference.label, reference.valueText]).toString();
  }

  /**
   * Each named entity's figure in the chosen period, for the timeline's legend where the ranking card is not
   * shown: the share then the value for a total ("44 % · 2.497", X1 / X2), the value for an average.
   */
  get comparisonLegendValues(): Record<string, string> | null {
    if (this.showRankingCard) {
      return null;
    }
    const values: Record<string, string> = {};
    (this.comparisonView?.rows ?? []).forEach((row) => {
      values[row.id] = row.shareText ? `${row.shareText} · ${row.valueText}` : row.valueText;
    });
    // The gesamt bar's figure (#2610137): the reference row's.
    const reference = this.comparisonView?.reference;
    if (reference && this.comparisonBarsEntities.some((entity) => entity.reference)) {
      values[reference.id] = reference.valueText;
    }
    return values;
  }

  /** The row dots of the ranking card: the timeline's colours, none where the timeline draws the levels. */
  get comparisonRankingColors(): Record<string, string> {
    return this.comparisonUsesLevels ? {} : this.comparisonColorMap;
  }

  /**
   * The one stable colour per entity id for the whole comparison, built once from the entities' size (their
   * projects in the chosen period). Every comparison card on every Mira screen reads this same map, so a
   * company / branch keeps its colour across the tiles and the screens while the comparison and period are
   * unchanged. Empty while no comparison is on. Theme-aware (re-reads `$vuetify.theme.dark`).
   *
   * The Markt is always a mid grey (Hans, #2610137: the market is the backdrop, not a company), and stays
   * out of the palette, so the company keeps the first colour.
   */
  get comparisonColorMap(): Record<string, string> {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const dark = this.$vuetify.theme.dark;
    const entities = this.comparisonBarsEntities;
    const market = this.showMarketComparison;
    if (!this.showComparison || entities.length === 0) {
      return {};
    }
    const sized: MiraSizedEntity[] = entities
      .filter((entity) => !(market && entity.id === MIRA_MARKET_PROJECTS_ID) && !entity.reference)
      .map((entity) => ({
        id: entity.id,
        label: entity.label,
        size: this.entitySize(entity.id),
      }));
    const colors = buildStableEntityColors(sized, dark);
    if (market) {
      colors[MIRA_MARKET_PROJECTS_ID] = dark ? COLOR_MIRA_MARKET_BAR_DARK : COLOR_MIRA_MARKET_BAR;
    }
    // The gesamt bar in a darker grey: the reference, not one of the segments (#2610137).
    if (entities.some((entity) => entity.reference)) {
      colors[MIRA_COMPARISON_REFERENCE_ID] = dark ? COLOR_MIRA_REFERENCE_BAR_DARK : COLOR_MIRA_REFERENCE_BAR;
    }
    return colors;
  }

  /** An entity's size in the chosen period (its projects, MIRA-PRJ-001), from the business-development source. */
  private entitySize(entityId: string): number | null {
    const period = this.selectedPeriod;
    if (this.showBranchComparison) {
      const metric = this.branchBusinessDocument?.periods[period]?.branches[entityId]?.[MIRA_SIZE_METRIC_ID];
      return metric &&
        metric.state === MiraAvailabilityState.AVAILABLE &&
        metric.value !== null &&
        metric.value !== undefined
        ? metric.value
        : null;
    }
    return publishedMetric(this.memberBusinessDocs[entityId]?.periods[period] ?? null, MIRA_SIZE_METRIC_ID)?.value ?? null;
  }

  /** Turn a comparison on or off. Loads the member documents the first time members are compared. */
  onComparisonChange(mode: MiraComparisonMode) {
    MiraComparisonModule.setMode(mode);
    if (mode === MIRA_COMPARISON_MEMBERS) {
      this.ensureMembersLoaded();
    }
  }

  /**
   * Load the member clients' names and their own section documents once per visit. The member reads are
   * swallowed per member in the repository, so one unreadable member drops out of the card rather than
   * blanking it.
   */
  async ensureMembersLoaded() {
    if (this.membersLoaded || !this.isGroupMaster) {
      return;
    }
    this.membersLoading = true;
    try {
      // A company's viewing client (#2610112 X6e) compares the company's Mandanten, named and copied into its
      // own documents by IRES Cloud: it may not read them as a master reads its members.
      const companyView = companyViewOf(this.sections);
      const loadMember = companyView ? loadCompanyMemberSection : loadMemberSection;
      let ids: string[];
      let names: Record<string, string>;
      if (companyView) {
        ({ ids, names } = companyViewRoster(companyView));
      } else {
        // The roster is the master's own `memberClientIds` (catalog S16.2: membership resolves from the master
        // document). The group registry only supplies names: it still lists removed members, because its
        // writes merge and a removal never clears an entry.
        ids = getMemberClientIds().filter((id) => !!id);
        const info = await getMemberClientsInformationOfGroup();
        names = {};
        info.forEach((client) => {
          const id = client.clientId;
          if (id && ids.includes(id)) {
            names[id] = (client.shortName || client.companyName || client.clientName || id).trim();
          }
        });
        // A member the registry does not name still counts, under its id, rather than silently vanishing.
        ids.forEach((id) => {
          if (!names[id]) names[id] = id;
        });
      }

      const documents = await Promise.all(ids.map((id) => loadMember(id, this.section)));
      const memberDocs: Record<string, MiraSectionDocument | null> = {};
      ids.forEach((id, index) => {
        memberDocs[id] = documents[index];
      });

      // Each member's business-development document sizes it for the stable colour order (its projects in
      // the period). The same cached read as the section above when this screen *is* Geschäftsentwicklung.
      const businessDocs: Record<string, MiraSectionDocument | null> = {};
      if (this.section === MiraSection.BUSINESS_DEVELOPMENT) {
        ids.forEach((id, index) => {
          businessDocs[id] = documents[index];
        });
      } else {
        const sizes = await Promise.all(ids.map((id) => loadMember(id, MiraSection.BUSINESS_DEVELOPMENT)));
        ids.forEach((id, index) => {
          businessDocs[id] = sizes[index];
        });
      }
      // A master can list itself among its members. Without its own figures it is not a company to
      // compare (the group row already stands for it), so it is left out rather than named "Ohne Wert".
      const masterId = getClientId();
      if (masterId && !memberDocs[masterId]) {
        delete names[masterId];
        delete memberDocs[masterId];
        delete businessDocs[masterId];
      }

      this.memberNames = names;
      this.memberSectionDocs = memberDocs;
      this.memberBusinessDocs = businessDocs;
      this.membersLoaded = true;
    } catch (error) {
      // The group list we cannot read leaves the card empty rather than failing the screen.
    } finally {
      this.membersLoading = false;
    }
  }

  /** When the comparison is already on (chosen on another screen), load this screen's member documents. */
  @Watch("isLoading")
  onLoadingSettled(loading: boolean) {
    if (!loading && this.showMemberComparison) {
      this.ensureMembersLoaded();
    }
  }

  /**
   * The Verlauf card's formatter for the figure it shows. A cached getter, never a call in the template:
   * a function built on every render is a new prop each time, so the chart rebuilt its data and replayed
   * its draw-in on every re-render of the screen (seen in the 2026-10-02 QA screenshots).
   */
  get activeTrendFormatter(): (value: number) => string {
    const metricId = this.activeTrendMetricId;
    return this.trendFormatter(metricId ?? "");
  }

  /**
   * The comparison stack's formatter for the selected total. A cached getter, never a call in the
   * template, for the same reason as {@link activeTrendFormatter}: a fresh function each render rebuilds
   * the chart and replays its draw-in.
   */
  get activeComparisonFormatter(): (value: number) => string {
    return this.trendFormatter(this.activeComparisonMetricId ?? "");
  }

  /**
   * Select the figure the Verlauf (or comparison) card shows. In comparison mode any period tile may be
   * chosen; otherwise only one with a trend, so the Verlauf card always has something to draw.
   */
  selectTrendMetric(metricId: string) {
    const selectable = this.comparisonActive
      ? this.periodTiles.some((tile) => tile.id === metricId)
      : this.trendAvailable(metricId);
    if (selectable) {
      this.selectedTrendMetricId = metricId;
    }
  }

  /**
   * The formatter the Verlauf card uses for its points, in the figure's own unit. Each screen provides
   * its own from its view model's `*TrendFormatter`; the base counts, as a safe fallback for a figure a
   * screen does not special-case.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trendFormatter(metricId: string): (value: number) => string {
    return (value) => formatNumber(value);
  }

  get periods(): string[] {
    return availablePeriods(this.document ?? undefined);
  }

  /**
   * The period this screen shows: the one chosen last on any Mira page, shared through the store
   * (#2610137), or this screen's first where its document lacks it. Choosing one here sets the shared
   * choice, so the landing page and the other screens follow.
   */
  get selectedPeriod(): string {
    return effectivePeriod(MiraPeriodModule.period, this.periods);
  }

  set selectedPeriod(period: string) {
    MiraPeriodModule.setPeriod(period);
  }

  get period(): MiraPublishedPeriod | null {
    // Both read before branching, so the computed tracks both (ui-conventions, Reactivity).
    const selected = this.selectedPeriod;
    const document = this.document;
    return document?.periods[selected] ?? null;
  }

  /** The chosen period's bounds, for the Verlauf card to shade on its fixed history. */
  get selectedRange(): { from: string; toExclusive: string } | null {
    const period = this.period;
    return period ? { from: period.from, toExclusive: period.toExclusive } : null;
  }

  /** The period the chosen one compares to, where the document carries it. */
  get comparison(): MiraPublishedPeriod | null {
    const key = this.period?.comparesTo;
    return key ? this.document?.periods[key] ?? null : null;
  }

  get periodDescription(): string {
    const period = this.period;
    if (!period) {
      return "";
    }

    const { first, last } = periodRange(period.from, period.toExclusive);
    return this.$t("mira.dataQuality.periodRange", [
      this.periodName(this.selectedPeriod),
      formatDateToDayMonthYear(first),
      formatDateToDayMonthYear(last),
    ]).toString();
  }

  get comparisonDescription(): string {
    const key = this.period?.comparesTo;
    return key && this.comparison ? this.$t("mira.dataQuality.comparedWith", [this.periodName(key)]).toString() : "";
  }

  get generatedAtDescription(): string {
    const generatedAt = this.document?.generatedAt;
    return generatedAt
      ? this.$t("mira.dataQuality.generatedAt", [formatDateToDayMonthYear(new Date(generatedAt))]).toString()
      : "";
  }

  /**
   * An IRES account's "Alle Mandanten" (#2610111): every published Mandant added up. No comparison, and
   * the parts that only make sense for one Mandant (Standorte, Projekttyp-Mix, the unlock list) are hidden.
   */
  get isAllClients(): boolean {
    return this.document?.scope === MIRA_ALL_CLIENTS_SCOPE;
  }

  /** Every section's figures waiting on data in the chosen period. A screen may narrow it. */
  get unlocks(): MiraSectionUnlocks[] {
    return collectUnlocks(this.sections, this.selectedPeriod);
  }

  /** The app bar's title in the user's language, with the edition after it (#2610137). */
  @Watch("$i18n.locale")
  setTitle() {
    setMiraToolbarTitle(this, this.titleKey);
  }

  async mounted() {
    this.setTitle();
    this.isGroupMaster = isMiraGroupMaster();

    try {
      this.sections = await loadMiraSections();

      // "Alle Mandanten" (#2610111) has no comparison, so it reads neither the Markt nor the branches.
      if (this.isAllClients) {
        return;
      }

      // The Markt document (#2610072), so the "Markt" option can appear; a missing one is `null`.
      // Geräte je Projekt is compared with the clients of the same kind (#2610112 X7): the Markt of the
      // client's kind takes its place, so every card below reads it unchanged.
      const own = this.sections[this.section] ?? null;
      this.marketProjectsDocument = withMarketOfOwnKind(await loadMarketSection("projects", this.section), own);

      // Load the branch document so the "Standorte" option can appear. One extra read per screen, and only
      // off a group master (its comparison is the group) and off Datenqualität (which never has one).
      if (!this.isGroupMaster && this.section !== MiraSection.DATA_QUALITY) {
        this.branchDocument = await loadBranchSection(this.section);
        // The business-development branch document sizes the branches for their stable colour order; the
        // same cached read as above when this screen *is* Geschäftsentwicklung.
        this.branchBusinessDocument =
          this.section === MiraSection.BUSINESS_DEVELOPMENT
            ? this.branchDocument
            : await loadBranchSection(MiraSection.BUSINESS_DEVELOPMENT);
      }

      // A member's view of its group (#2610112 X6d), so the "Gruppe" option can appear. Off a master only.
      if (!this.isGroupMaster) {
        this.groupViewDocument = await loadGroupViewSection(this.section);
      }
    } catch (error) {
      SnackbarModule.showError({ key: "mira.dataQuality.loadError" });
    } finally {
      this.isLoading = false;
    }
  }

  periodName(period: string): string {
    return miraPeriodName(period, (key) => this.$t(key).toString());
  }
}
